import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ChoquesSection} from '@/components/choques-section'
import {DataTable, type Column} from '@/components/data-table'
import {ListEmptyState} from '@/components/section-card'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {CUMPLIMIENTO_POR_PAGINA, type NoVerificable} from '@/hooks/empresa/cumplimiento/use-cumplimiento'
import {formatDateOnly} from '@/lib/risk'

type Linea = NoVerificable & {indice: number}

/** El choque de restricción médica (D12) vive aquí: es el destino del botón «Resolver el choque»
 * de una fila y tiene que existir en el DOM. */
export function SinVerificarTab({
  businessId,
  employeeId,
  lineas,
  canEdit,
  onFijarCifra,
}: {
  businessId: string
  employeeId: string
  lineas: NoVerificable[]
  canEdit: boolean
  onFijarCifra: (cifraKey: string) => void
}) {
  const {t} = useTranslation()
  const [page, setPage] = useState(1)
  const filas: Linea[] = lineas.map((linea, indice) => ({...linea, indice}))
  const ultima = Math.max(1, Math.ceil(filas.length / CUMPLIMIENTO_POR_PAGINA))
  const actual = Math.min(page, ultima)
  const desde = (actual - 1) * CUMPLIMIENTO_POR_PAGINA
  const columnas: Column<Linea>[] = [
    {
      key: 'motivo',
      header: t('cumplimiento.columna.motivo'),
      cell: (linea) => (
        <p className="text-sm">
          {t(`cumplimiento.noVerificable.${linea.motivo}`, {
            nombre: linea.nombre,
            fecha: linea.fecha ? formatDateOnly(linea.fecha) : '',
            // Sin `count` i18next no resuelve el plural de la línea de custodia y pinta la clave cruda.
            count: Number(linea.nombre) || 0,
          })}
        </p>
      ),
    },
    {
      key: 'accion',
      header: t('cumplimiento.columna.accion'),
      width: 'w-36',
      className: 'text-right',
      stopClick: true,
      cell: (linea) =>
        linea.cifra_key && canEdit ? (
          <button
            type="button"
            className="text-xs underline"
            onClick={() => onFijarCifra(linea.cifra_key!)}
            data-testid={`cumplimiento-fijar-${linea.cifra_key}`}>
            {t('cumplimiento.noVerificable.fijarCifra')}
          </button>
        ) : null,
    },
  ]
  return (
    <section className="space-y-4" data-testid="cumplimiento-no-verificable">
      <DataTable
        columns={columnas}
        rows={filas.slice(desde, desde + CUMPLIMIENTO_POR_PAGINA)}
        rowKey={(linea) => `${linea.motivo}-${linea.indice}`}
        emptyState={<ListEmptyState title={t('cumplimiento.noVerificable.vacio')} />}
      />
      {filas.length > 0 && (
        <PaginationBar page={actual} pageSize={CUMPLIMIENTO_POR_PAGINA} total={filas.length} onPageChange={setPage} />
      )}
      <ChoquesSection businessId={businessId} employeeId={employeeId} />
    </section>
  )
}
